import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import RequireAuth from "./components/RequireAuth";
import AppLayout from "./components/AppLayout";
import CookieBanner from "./components/CookieBanner";
import { queryKeys } from "../../shared/api/queryKeys";
import { getJobAd } from "../../shared/api/endpoints/jobs";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordConfirmPage = lazy(
  () => import("./pages/ResetPasswordConfirmPage"),
);
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const NewJobPage = lazy(() => import("./pages/NewJobPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const GeneratePage = lazy(() => import("./pages/GeneratePage"));
const CoachingPage = lazy(() => import("./pages/CoachingPage"));
const ProfileOptimizerPage = lazy(() => import("./pages/ProfileOptimizerPage"));
const InterviewPrepPage = lazy(() => import("./pages/InterviewPrepPage"));
const GenerationResultPage = lazy(() => import("./pages/GenerationResultPage"));
const GenerationsHistoryPage = lazy(
  () => import("./pages/GenerationsHistoryPage"),
);
const PricingPage = lazy(() => import("./pages/PricingPage"));
const AccountSubscriptionPage = lazy(
  () => import("./pages/AccountSubscriptionPage"),
);
const PipelinePage = lazy(() => import("./pages/PipelinePage"));
const RoleWorkspacePage = lazy(() => import("./pages/RoleWorkspacePage"));
const NetworkPage = lazy(() => import("./pages/NetworkPage"));
const PersonDetailPage = lazy(() => import("./pages/PersonDetailPage"));
const InboundReviewPage = lazy(() => import("./pages/InboundReviewPage"));
const CompanyDetailPage = lazy(() => import("./pages/CompanyDetailPage"));
const GroupDetailPage = lazy(() => import("./pages/GroupDetailPage"));
const ImportsListPage = lazy(() => import("./pages/ImportsListPage"));
const ImportDetailPage = lazy(() => import("./pages/ImportDetailPage"));
const BrainDumpPage = lazy(() => import("./pages/BrainDumpPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const MFAVerifyPage = lazy(() => import("./pages/MFAVerifyPage"));
const AccountSecurityPage = lazy(() => import("./pages/AccountSecurityPage"));
const AccountAPIKeysPage = lazy(() => import("./pages/AccountAPIKeysPage"));
const EmailPreferencesPage = lazy(() => import("./pages/EmailPreferencesPage"));
const TrashPage = lazy(() => import("./pages/TrashPage"));

function LoadingSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
    </div>
  );
}

// Redirect that preserves a route param (React Router's <Navigate> can't interpolate).
function ParamRedirect({
  build,
}: {
  build: (params: Readonly<Record<string, string | undefined>>) => string;
}) {
  const params = useParams();
  return <Navigate to={build(params)} replace />;
}

// /jobs/:uuid → resolve the linked Opportunity and land in its workspace.
// Falls back to the legacy JobDetailPage for jobs with no opportunity yet
// (until Workstream B auto-creates one on JobAd create).
function JobWorkspaceRedirect() {
  const { uuid } = useParams<{ uuid: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.jobs.detail(uuid!),
    queryFn: () => getJobAd(uuid!),
    enabled: !!uuid,
  });
  if (isLoading) return <LoadingSpinner />;
  if (isError || !data) return <Navigate to="/pipeline" replace />;
  if (data.opportunity_uuid)
    return <Navigate to={`/pipeline/${data.opportunity_uuid}`} replace />;
  return <JobDetailPage />;
}

export default function App() {
  return (
    <>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public marketing routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/legal/:section" element={<LegalPage />} />

          {/* Public app routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/reset-password/:uid/:token"
            element={<ResetPasswordConfirmPage />}
          />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/auth/mfa/verify" element={<MFAVerifyPage />} />

          {/* Protected routes - RequireAuth checks session; 401 → /login?next=... */}
          <Route element={<RequireAuth />}>
            {/* All protected routes share the persistent top nav */}
            <Route element={<AppLayout />}>
              <Route path="/onboarding" element={<OnboardingPage />} />

              {/* Dashboard (absorbs Today) */}
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route
                path="/today"
                element={<Navigate to="/dashboard" replace />}
              />

              {/* Profile + library */}
              <Route path="/profile" element={<ProfilePage />} />
              <Route
                path="/profile/accomplishments/brain-dump"
                element={<BrainDumpPage />}
              />

              {/* Pipeline (interim targets — Workstreams B/C replace these pages) */}
              <Route path="/pipeline" element={<PipelinePage />} />
              <Route path="/pipeline/new" element={<NewJobPage />} />
              <Route path="/pipeline/tasks" element={<PipelinePage />} />
              <Route path="/pipeline/review" element={<PipelinePage />} />
              <Route path="/pipeline/:uuid" element={<RoleWorkspacePage />} />

              {/* Network (interim targets — Workstream D replaces with NetworkPage) */}
              <Route
                path="/network"
                element={<Navigate to="/network/people" replace />}
              />
              <Route path="/network/people" element={<NetworkPage />} />
              <Route
                path="/network/people/:uuid"
                element={<PersonDetailPage />}
              />
              <Route path="/network/companies" element={<NetworkPage />} />
              <Route
                path="/network/companies/:uuid"
                element={<CompanyDetailPage />}
              />
              <Route path="/network/groups" element={<NetworkPage />} />
              <Route
                path="/network/groups/:uuid"
                element={<GroupDetailPage />}
              />
              <Route path="/network/imports" element={<ImportsListPage />} />
              <Route
                path="/network/imports/:uuid"
                element={<ImportDetailPage />}
              />

              {/* Generation (still job-keyed; Workstream B folds these into the workspace) */}
              <Route path="/jobs/:uuid/generate" element={<GeneratePage />} />
              <Route
                path="/jobs/:uuid/generations"
                element={<GenerationsHistoryPage />}
              />
              <Route
                path="/generations/:uuid"
                element={<GenerationResultPage />}
              />
              <Route path="/coaching/:sessionUuid" element={<CoachingPage />} />

              {/* LinkedIn Profile Optimizer (Phase 5) */}
              <Route
                path="/profile-optimizer"
                element={<ProfileOptimizerPage />}
              />

              {/* Interview Coach (Phase 6) */}
              <Route
                path="/interview-prep/:prepUuid"
                element={<InterviewPrepPage />}
              />

              {/* Account */}
              <Route
                path="/account/subscription"
                element={<AccountSubscriptionPage />}
              />
              <Route
                path="/account/security"
                element={<AccountSecurityPage />}
              />
              <Route
                path="/account/api-keys"
                element={<AccountAPIKeysPage />}
              />
              <Route
                path="/account/email-preferences"
                element={<EmailPreferencesPage />}
              />

              {/* Misc */}
              <Route path="/inbound" element={<InboundReviewPage />} />
              <Route path="/trash" element={<TrashPage />} />

              {/* ---- Legacy redirects (keep bookmarks/emails working) ---- */}
              <Route
                path="/tracker"
                element={<Navigate to="/pipeline" replace />}
              />
              <Route
                path="/tracker/opportunities/:uuid"
                element={<ParamRedirect build={(p) => `/pipeline/${p.uuid}`} />}
              />
              <Route
                path="/tracker/tasks"
                element={<Navigate to="/pipeline/tasks" replace />}
              />
              <Route
                path="/tracker/review"
                element={<Navigate to="/pipeline/review" replace />}
              />
              <Route
                path="/tracker/people"
                element={<Navigate to="/network/people" replace />}
              />
              <Route
                path="/tracker/people/:uuid"
                element={
                  <ParamRedirect build={(p) => `/network/people/${p.uuid}`} />
                }
              />
              <Route
                path="/tracker/groups"
                element={<Navigate to="/network/groups" replace />}
              />
              <Route
                path="/tracker/groups/:uuid"
                element={
                  <ParamRedirect build={(p) => `/network/groups/${p.uuid}`} />
                }
              />
              <Route
                path="/jobs"
                element={<Navigate to="/pipeline" replace />}
              />
              <Route
                path="/jobs/new"
                element={<Navigate to="/pipeline/new" replace />}
              />
              <Route path="/jobs/:uuid" element={<JobWorkspaceRedirect />} />
              <Route
                path="/companies"
                element={<Navigate to="/network/companies" replace />}
              />
              <Route
                path="/companies/:uuid"
                element={
                  <ParamRedirect
                    build={(p) => `/network/companies/${p.uuid}`}
                  />
                }
              />
              <Route
                path="/documents"
                element={<Navigate to="/profile" replace />}
              />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      <CookieBanner />
    </>
  );
}
